import { Empty, message, Skeleton } from "antd";
import { AttachedFiles, type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import {
    forwardRef,
    type Ref,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom-v5-compat";
import { Screen, ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { BTDropdownButton, BTText } from "@btnet/core-components";
import { useDiscussionsByExternalIdGetV2 } from "@btnet/services-btnet/handlers/discussions/discussions";
import { LinkedTypes } from "@btnet/services-btnet/handlers/models/linkedTypes";
import type { SpecificationMappingData } from "@btnet/services-btnet/handlers/models/specificationMappingData";
import type { SpecificationResponse } from "@btnet/services-btnet/handlers/models/specificationResponse";
import { SpecificationStatus as SpecificationStatusResponse } from "@btnet/services-btnet/handlers/models/specificationStatus";
import {
    getSpecificationsByIdGetV2QueryKey,
    getSpecificationsLookupListGetV2QueryKey,
    specificationsByIdGetV2,
    useSpecificationsSharetokenPostV2,
} from "@btnet/services-btnet/handlers/specifications/specifications";
import { cn } from "@btnet/utilities-core";

import {
    BdsActionBar,
    BdsButtonGroup,
    BdsContentHeader,
    BdsFloatAlign,
    BdsIconClockCounterClockwise,
    BdsIconComments,
    BdsIconCommentsFilled,
    BdsIconPencilSimple,
    BdsIconShare,
    BdsIconX,
    BdsLayout,
    BdsRollupButton,
} from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { queryClient } from "helpers/queryClient";

import { BTLoginTypes } from "types/enum";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { showAPIErrorMessage } from "utilities/apiHandler";
import { useActionBeingPerformed } from "utilities/form/form";
import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";
import { routes } from "utilities/routes";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { BTSplitView } from "commonComponents/btWrappers/BTSplitView/BTSplitView";
import { type BTSplitViewMode } from "commonComponents/btWrappers/BTSplitView/BTSplitView.types";
import { EditorContent } from "commonComponents/btWrappers/editor/editor.types";
import CommentComponent from "commonComponents/entity/comment/CommentComponent";
import { isOpenCommentsEnabled } from "commonComponents/entity/comment/CommentComponent.utils";
import { CommentsButtonForPage } from "commonComponents/entity/comment/CommentComponentButtons";
import {
    DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { HotkeyCommands } from "commonComponents/utilities/Hotkey/hotkey.utility";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";
import { StickyProvider } from "commonComponents/utilities/StickyContext";

import { writeToClipboard } from "entity/media/common/media.utils";
import { useSaveSpecificationToCatalogWithConfirm } from "entity/specification/common/saveSpecificationToCatalog";
import { SpecificationDisplay } from "entity/specification/common/SpecificationDisplay";
import { SpecificationStatus } from "entity/specification/common/SpecificationStatus";
import { useSpecificationByIdEndpointSelector } from "entity/specification/shared/hooks/useSpecificationApiEndpointSelector";
import {
    SpecificationHandler,
    SpecificationSaveRequest,
    SpecificationUpdateRequest,
} from "entity/specification/Specification.api.handler";
import {
    type ISpecificationFormValues,
    type SpecificationDetailsFormActions,
    type SpecificationDisplayActionType,
    SpecificationOwnerShareMode,
    SpecificationSubShareMode,
} from "entity/specification/Specification.api.types";

import "./SpecificationViewer.less";

import BTSpecificationShareQRCode from "./BTSpecificationShareQRCode";
import { SpecificationInfoButton } from "./SpecificationInfoButton";
import {
    IN_PANEL_MODES,
    SPECIFICATION_PANEL_CONFIG_BY_MODE,
    type SpecificationPanelMode,
} from "./specificationPanelConfig";
import { SpecificationVersionHistory } from "./SpecificationVersionHistory";
import { SpecificationViewerSkeleton } from "./SpecificationViewerSkeleton";
import {
    canShowUnpublishedChangesBanner,
    canShowVersionHistoryButton,
    useSpecificationActionVisibility,
} from "./useSpecificationActionVisibility";
import { useSpecificationCreateDefaults } from "./useSpecificationCreateDefaults";
import { useSpecificationInlineEdit } from "./useSpecificationInlineEdit";
import { useSpecificationSnapshot } from "./useSpecificationSnapshot";

/**
 * Which (if any) secondary panel is open on the right side of the viewer. The three options are
 * mutually exclusive — opening one closes the other.
 */
type RightPanel = "none" | "comments" | "versionHistory";

// Modal render function to prevent click propagation
const preventClickPropagationModalRender = (node: React.ReactNode) => (
    <div onClick={(e) => e.stopPropagation()}>{node}</div>
);

export interface ISpecificationViewerRef {
    guardNavigation: (action: () => void) => void;
}

interface ISpecificationViewerProps {
    // External mode props
    shareToken?: string;
    externalId?: string;

    // Internal mode props
    specificationId: number | null;
    jobId: number;
    bidId?: number;

    // Common props
    onUpdated?: (spec: SpecificationResponse) => void;
    onBack?: () => void;
    onDelete?: () => void;
    useMobileLayout?: boolean;
    isMobileWebview?: boolean;
    jobName?: string;
    handler?: SpecificationHandler;

    /**
     * Which host is rendering the viewer; drives both the compact panel *layout* (BTItemHeader, no
     * outer split chrome — every mode except `bookView`) and which secondary features/actions the
     * panel surfaces. Defaults to `bookView` (the full-page book view).
     */
    mode?: SpecificationPanelMode;
    /** Opens an existing spec directly in edit state (e.g. the grid row-actions Edit pencil). */
    initialEditMode?: boolean;

    // Inline-create props.
    /** Renders an empty form from defaults instead of fetching by id; Save calls handler.create. */
    isCreating?: boolean;
    /** Whether the spec belongs to the general job (e.g. Cost Group). Hides actions and "Viewable By" that don't apply there. */
    isGeneralJob?: boolean;
    /** Pre-populated mapping data (estimate-group context). */
    specificationMapping?: SpecificationMappingData;
    /** Fires after a new spec has been created and refetched. */
    onCreated?: (spec: SpecificationResponse) => void;
    /** Fires when the user discards the in-progress new spec (Cancel / Back). */
    onCancelCreate?: () => void;
}

// forwardRef exposes guardNavigation to parent components
// (SpecificationBook, SpecificationsPanelLayout) for unsaved-changes guards.
export const SpecificationViewer = forwardRef<ISpecificationViewerRef, ISpecificationViewerProps>(
    function SpecificationViewer(
        {
            shareToken,
            externalId,
            specificationId,
            jobId,
            bidId,
            onUpdated,
            onBack,
            onDelete,
            useMobileLayout = false,
            isMobileWebview = false,
            handler,
            mode = "bookView",
            initialEditMode = false,
            isCreating = false,
            isGeneralJob = false,
            specificationMapping,
            onCreated,
            onCancelCreate,
        }: Readonly<ISpecificationViewerProps>,
        ref: Ref<ISpecificationViewerRef>,
    ) {
        const specificationHandler = useMemo(
            () => handler ?? new SpecificationHandler(),
            [handler],
        );

        const isPanelMode = IN_PANEL_MODES.has(mode);
        const panelConfig = SPECIFICATION_PANEL_CONFIG_BY_MODE[mode];

        // Detect if we're in external mode (shareToken provided)
        const isExternalMode = !!shareToken;

        // Below lg breakpoint, viewer container is too narrow for split — use mobile-style full takeover.
        const isNarrowDesktopViewport = useMediaQuery(`(max-width: ${ScreenMax.lg}px)`);
        const useMobileVersionHistoryPanel = useMobileLayout || isNarrowDesktopViewport;

        // Panel mode: the in-panel split collapses to a single pane when the PANEL (not the viewport)
        // is too narrow for side-by-side. We track the panel's own width so that, while collapsed,
        // selecting a version can swap the visible pane to the snapshot preview — mirroring the
        // book view's narrow-viewport takeover, but keyed on container width (a narrow panel on a
        // wide screen must behave the same as a narrow screen). The shared useResizeObserver returns
        // a ref-change handler that (re)attaches the observer whenever the panel node mounts — needed
        // because the panel renders after a loading-skeleton early-return.
        const [isPanelNarrow, setIsPanelNarrow] = useState(false);
        const [, panelWrapperRefHandler] = useResizeObserver<HTMLDivElement>((entry) => {
            const width = entry?.contentRect.width ?? 0;
            setIsPanelNarrow(width > 0 && width < Screen.md);
        });

        const userInfo = useRequiredContext(UserInfoContext);
        const isBuilder = userInfo.loginType === BTLoginTypes.BUILDER;
        const builderInfo = useContext(BuilderInfoContext);
        const isGlobalSpecCatalogEnabled = builderInfo?.flags.globalSpecCatalog === true;

        const canUpdate = usePermission("specifications", "canUpdate");

        // A read-only panel config (e.g. the catalog import preview) makes the whole surface
        // view-only regardless of tab permissions — suppresses actions, blocks edit entry, and
        // makes attachments read-only.
        const isPanelReadonly = isPanelMode && !panelConfig.canEdit;

        const { saveToCatalogWithConfirm } = useSaveSpecificationToCatalogWithConfirm();
        const { mutateAsync: getSpecShareToken } = useSpecificationsSharetokenPostV2();

        const [actionBeingPerformed, setActionBeingPerformed] =
            useActionBeingPerformed<SpecificationDetailsFormActions>();

        const [activeAction, setActiveAction] = useState<SpecificationDisplayActionType>(undefined);
        const [isDirty, setIsDirty] = useState(false);
        const [currentFiles, setCurrentFiles] = useState<BTFileSystem[] | null>(null);
        const [showShareQRCode, setShowShareQRCode] = useState(false);
        const location = useLocation();

        // Resolved by handleActionComplete after save — lets dirty-prompt await "Save" before navigating away
        const saveCompleteRef = useRef<(() => void) | null>(null);

        const requestSave = useCallback(() => {
            return new Promise<void>((resolve) => {
                saveCompleteRef.current = resolve;
                setActiveAction("save");
            });
        }, []);

        // Stop arrow key events from propagating to the document-level hotkey handler,
        // which would scroll the page instead of moving the cursor in the editor.
        const handleEditorArrowKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
            const isArrowKey =
                e.key === "ArrowLeft" ||
                e.key === "ArrowRight" ||
                e.key === "ArrowUp" ||
                e.key === "ArrowDown";
            if (isArrowKey) {
                e.stopPropagation();
            }
        }, []);

        const { isEditMode, enterEditMode, exitEditMode, handleSubmit, handleCancel, handleBack } =
            useSpecificationInlineEdit({
                isDirty,
                requestSave,
                onBack,
                viewerRef: ref,
                isCreating,
                // A read-only panel can never start (or enter) edit mode.
                initialEditMode: initialEditMode && !isPanelReadonly,
                onCancelCreate,
            });

        const {
            data: specificationResponse,
            isLoading: isLoadingExisting,
            error,
        } = useSpecificationByIdEndpointSelector(
            shareToken,
            externalId,
            isCreating ? null : specificationId,
            isExternalMode ? undefined : jobId,
            isExternalMode ? undefined : bidId,
        );

        const { defaults: createDefaults, isLoading: isLoadingCreateDefaults } =
            useSpecificationCreateDefaults({
                enabled: isCreating,
                jobId,
                specificationMapping,
            });

        useEffect(() => {
            if (error) {
                showApixErrorMessage(error);
            }
        }, [error]);

        const isLoading = isCreating ? isLoadingCreateDefaults : isLoadingExisting;
        const specification = isCreating ? createDefaults : specificationResponse?.data;

        // Comments integration
        const [searchParams] = useSearchParams();
        const { data: discussionResponse, refetch: refetchDiscussion } =
            useDiscussionsByExternalIdGetV2(
                specification?.externalId ?? "",
                { linkedType: LinkedTypes.Specification },
                {
                    query: {
                        enabled: !isExternalMode && !isCreating && !!specification?.externalId,
                    },
                },
            );

        const discussion = discussionResponse?.data;
        const discussionEntity: IHasDiscussionContainer | undefined = discussion
            ? {
                  discussions: new DiscussionsEntity(discussion.discussion),
                  instanceKey: `specification-${discussion.linkedId}`,
                  id: discussion.linkedId,
                  builderId: discussion.builderId,
                  jobId: discussion.discussion.jobId ?? undefined,
              }
            : undefined;
        const hasComments =
            !!discussionEntity && discussionEntity.discussions.comments.comments.length > 0;

        // Panel mode hides the right-hand panels (Version History / Comments) unless the panel
        // config opts into them.
        const suppressRightPanels =
            isPanelMode && !panelConfig.versionHistory && !panelConfig.comments;
        const [rightPanel, setRightPanel] = useState<RightPanel>(() =>
            !isExternalMode && !suppressRightPanels && isOpenCommentsEnabled(searchParams)
                ? "comments"
                : "none",
        );
        const isCommentsOpen = rightPanel === "comments";
        const isVersionHistoryOpen = rightPanel === "versionHistory";

        const {
            selectedSnapshotId,
            setSelectedSnapshotId,
            activeSnapshotData,
            isShowingSnapshotContent,
            isSnapshotFetching,
        } = useSpecificationSnapshot({
            specificationExternalId: specification?.externalId ?? "",
            jobId,
            isVersionHistoryOpen,
        });

        const toggleRightPanel = useCallback((panel: Exclude<RightPanel, "none">) => {
            setRightPanel((prev) => (prev === panel ? "none" : panel));
        }, []);

        const handleCommentsClick = useCallback(
            () => toggleRightPanel("comments"),
            [toggleRightPanel],
        );
        const handleVersionHistoryClick = useCallback(
            () => toggleRightPanel("versionHistory"),
            [toggleRightPanel],
        );

        const handleUpdateCommentData = useCallback(async () => {
            await refetchDiscussion();
        }, [refetchDiscussion]);

        const invalidateQueries = useCallback(async () => {
            if (specificationId == null) return;
            // Fire-and-forget: the TOC/selector list refreshes in the background
            void queryClient.invalidateQueries({
                queryKey: getSpecificationsLookupListGetV2QueryKey({ jobId }),
            });
            // Awaited: callers read fresh data from this cache (via queryClient.getQueryData) for onUpdated
            await queryClient.invalidateQueries({
                queryKey: getSpecificationsByIdGetV2QueryKey(specificationId, { jobId }),
            });
        }, [jobId, specificationId]);

        const handleActionComplete = useCallback(
            (action: SpecificationDetailsFormActions) => {
                setActiveAction(undefined);
                if (action === "save" || action === "publish") {
                    exitEditMode();
                    saveCompleteRef.current?.();
                    saveCompleteRef.current = null;
                }
            },
            [exitEditMode],
        );

        const handleEnterEditMode = useCallback(() => {
            if (!specification) return;
            setRightPanel("none");
            enterEditMode();
        }, [specification, enterEditMode]);

        const initialValues = useMemo((): ISpecificationFormValues => {
            if (activeSnapshotData) {
                return {
                    specificationContent: EditorContent.FromApix(activeSnapshotData.content),
                    ownerShareMode: activeSnapshotData.ownerShareMode,
                    subShareMode: activeSnapshotData.subShareMode,
                    usersToNotify: [],
                };
            }
            if (!specification) {
                return {
                    specificationContent: EditorContent.FromPlainText(""),
                    ownerShareMode: SpecificationOwnerShareMode.NotShared,
                    subShareMode: SpecificationSubShareMode.NotShared,
                    usersToNotify: [],
                };
            }
            // Editors on specs with unpublished changes get `publishedContent` alongside `content`.
            // View mode shows the last-published snapshot; edit mode shows the live draft.
            const viewContent =
                !isEditMode && specification.publishedContent
                    ? specification.publishedContent
                    : specification.content;
            return {
                specificationContent: EditorContent.FromApix(viewContent),
                ownerShareMode: specification.ownerShareMode,
                subShareMode: specification.subShareMode,
                usersToNotify: [],
            };
        }, [specification, activeSnapshotData, isEditMode]);

        const handleCreateSpec = useCallback(
            async (
                values: ISpecificationFormValues,
                action?: SpecificationDetailsFormActions,
            ): Promise<boolean> => {
                try {
                    await setActionBeingPerformed({
                        actionBeingPerformed: action ?? "save",
                        callback: async () => {
                            const createRequest = new SpecificationSaveRequest(jobId, values);
                            const createResult = await specificationHandler.create(createRequest);

                            // Seed the by-id cache so the viewer hits cache when it mounts on
                            // the new spec instead of refetching. Wrapped because the spec
                            // exists on the server even if this fetch 500s; a generic error
                            // here would mislead the user into thinking the create itself
                            // failed.
                            let response;
                            try {
                                response = await queryClient.fetchQuery({
                                    queryKey: getSpecificationsByIdGetV2QueryKey(createResult.id, {
                                        jobId,
                                    }),
                                    queryFn: ({ signal }) =>
                                        specificationsByIdGetV2(
                                            createResult.id,
                                            { jobId },
                                            undefined,
                                            signal,
                                        ),
                                });
                            } catch (refreshError) {
                                void message.error(
                                    "Specification created, but failed to refresh. Please reload the page.",
                                );
                                showAPIErrorMessage(refreshError);
                                return;
                            }

                            void message.success(
                                action === "publish"
                                    ? "Specification published"
                                    : "Specification created",
                            );

                            void queryClient.invalidateQueries({
                                queryKey: getSpecificationsLookupListGetV2QueryKey({ jobId }),
                            });

                            onCreated?.(response.data);
                        },
                    });
                    return true;
                } catch (error) {
                    showAPIErrorMessage(error);
                    return false;
                }
            },
            [jobId, specificationHandler, setActionBeingPerformed, onCreated],
        );

        const handleSaveSpec = useCallback(
            async (
                values: ISpecificationFormValues,
                action?: SpecificationDetailsFormActions,
            ): Promise<boolean> => {
                if (isCreating) {
                    return handleCreateSpec(values, action);
                }
                if (!specification) return false;
                try {
                    await setActionBeingPerformed({
                        actionBeingPerformed: action ?? "save",
                        callback: async () => {
                            // View-mode saves use live content, not the form's (may be snapshot).
                            const effectiveValues = !isEditMode
                                ? {
                                      ...values,
                                      specificationContent: EditorContent.FromApix(
                                          specification.content,
                                      ),
                                  }
                                : values;
                            const updateRequest = new SpecificationUpdateRequest(
                                specification.id,
                                specification.jobId,
                                effectiveValues,
                                action === "publish"
                                    ? SpecificationStatus.Published
                                    : SpecificationStatus.Draft,
                            );
                            await specificationHandler.update(updateRequest);

                            const successMessages: Partial<
                                Record<NonNullable<SpecificationDetailsFormActions>, string>
                            > = {
                                publish: "Specification published",
                                unpublish: "Specification unpublished",
                                share: "Viewing permissions updated",
                            };
                            void message.success(
                                (action && successMessages[action]) ?? "Specification saved",
                            );

                            await invalidateQueries();

                            if (onUpdated) {
                                const freshData = queryClient.getQueryData<{
                                    data: SpecificationResponse;
                                }>(
                                    getSpecificationsByIdGetV2QueryKey(specification.id, {
                                        jobId: specification.jobId,
                                    }),
                                );
                                if (freshData?.data) {
                                    onUpdated(freshData.data);
                                }
                            }
                        },
                    });
                    return true;
                } catch (error) {
                    showAPIErrorMessage(error);
                    return false;
                }
            },
            [
                isCreating,
                handleCreateSpec,
                specification,
                setActionBeingPerformed,
                isEditMode,
                specificationHandler,
                invalidateQueries,
                onUpdated,
            ],
        );

        const attachedFiles = useMemo(() => {
            // Attachments aren't versioned — hide when viewing a historical snapshot.
            if (selectedSnapshotId) return null;
            if (!specification?.attachedFiles) return null;
            const base = new AttachedFiles(specification.attachedFiles);
            if (currentFiles) {
                base.files = currentFiles;
            }
            return base;
        }, [specification?.attachedFiles, currentFiles, selectedSnapshotId]);

        const actionVisibility = useSpecificationActionVisibility({
            specification,
            isExternalMode,
            isEditMode,
            isMobileWebview,
            isPanelMode,
            panelConfig,
            isBuilder,
            isGeneralJob,
            isGlobalSpecCatalogEnabled,
            selectedSnapshotId,
        });
        const showVersionHistoryButton = canShowVersionHistoryButton({
            isBuilder,
            isExternalMode,
            isEditMode,
            isPanelMode,
            showVersionHistory: panelConfig.versionHistory,
        });
        const showUnpublishedChangesBanner = canShowUnpublishedChangesBanner({
            spec: specification,
            isPublished: actionVisibility.isPublished,
            isBuilder,
            isExternalMode,
            isEditMode,
            selectedSnapshotId,
        });
        // In panel mode the Comments button only shows when the config opts in; outside panel mode
        // (book view) comments always apply.
        const showCommentsButton =
            !isExternalMode &&
            !isEditMode &&
            !selectedSnapshotId &&
            (!isPanelMode || panelConfig.comments);

        // Delete action handler
        const handleDelete = useCallback(() => {
            if (!specification) return;

            const specTitle = specification.title ?? "this specification";
            const content = isGeneralJob
                ? `Are you sure you want to delete "${specTitle}"? Any cost groups linked to this spec at the company level will lose the spec link.`
                : `Are you sure you want to delete "${specTitle}"?`;

            btDeleteConfirm({
                entityName: "Specification",
                content,
                modalRender: preventClickPropagationModalRender,
                onOk: async () => {
                    try {
                        await setActionBeingPerformed({
                            actionBeingPerformed: "delete",
                            callback: async () => {
                                await specificationHandler.delete({
                                    id: specification.id,
                                    jobId: specification.jobId,
                                });
                                void message.success("Specification deleted");
                                onDelete?.();
                                onBack?.();
                            },
                        });
                    } catch (error) {
                        showAPIErrorMessage(error);
                    }
                },
            });
        }, [
            specification,
            isGeneralJob,
            specificationHandler,
            onDelete,
            onBack,
            setActionBeingPerformed,
        ]);

        // Print action handler
        const handlePrint = useCallback(() => {
            if (!specification) return;
            window.open(
                routes.appBase + routes.specification.getPrintLink([specification.id]),
                "_blank",
                "noopener,noreferrer",
            );
        }, [specification]);

        const handleCopySpecShareLink = useCallback(async () => {
            if (!specification) return;

            const getShareLink = async (): Promise<string> => {
                const response = await getSpecShareToken({
                    params: { jobIdParam: specification.jobId },
                });
                return response.data.shareToken
                    ? `${window.location.origin}${routes.appBase}${routes.specification.getExternalLink(response.data.shareToken, specification.externalId)}`
                    : "";
            };

            await writeToClipboard(getShareLink(), "Share link copied");
        }, [specification, getSpecShareToken]);

        // Save to catalog: copies this job-level spec up to the company catalog as a new, independent spec.
        const handleSaveToCatalog = useCallback(async () => {
            if (!specification) return;

            await setActionBeingPerformed({
                actionBeingPerformed: "saveToCatalog",
                callback: async () => {
                    await saveToCatalogWithConfirm({
                        id: specification.id,
                        jobId: specification.jobId,
                    });
                },
            });
        }, [specification, saveToCatalogWithConfirm, setActionBeingPerformed]);

        // Skeleton while loading or with no id. Create mode skips the id guard — form is
        // populated from the defaults endpoint.
        if ((!isCreating && !specificationId && !externalId) || isLoading) {
            return (
                <SpecificationViewerSkeleton
                    useMobileLayout={useMobileLayout}
                    showBackButton={!!onBack}
                />
            );
        }

        if (!specification) {
            return (
                <div className="specification-viewer" data-testid="specification-viewer-empty">
                    <Empty
                        description="Select a specification from the table of contents to view"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                </div>
            );
        }

        const isPublished = specification.status === SpecificationStatusResponse.Published;
        const statusType: "success" | "default" =
            !isExternalMode && isPublished ? "success" : "default";
        const statusProps = isEditMode
            ? {}
            : {
                  statusMessage: isExternalMode ? "" : isPublished ? "Published" : "Draft",
                  statusType,
                  statusTestId: "specification-viewer-status" as const,
              };
        const displayJobName = specification.jobInfo.name;

        const headerActions = (
            <>
                {actionVisibility.showActionsMenu && (
                    <BdsButtonGroup
                        id="specification-viewer-actions"
                        testid="specification-viewer-actions"
                        menuTestid="specification-viewer-actions-menu"
                        rollupTestid="specification-viewer-actions-rollup"
                        displayType="tertiary"
                        align={BdsFloatAlign.BR}
                    >
                        {actionVisibility.showDelete && (
                            <BdsRollupButton
                                id="specification-viewer-delete"
                                testid="specification-viewer-delete"
                                ariaLabel="Delete"
                                displayType="secondarydanger"
                                text="Delete"
                                onClick={handleDelete}
                                disabled={!!actionBeingPerformed}
                                hotkey={HotkeyCommands.delete.command}
                                value="delete"
                                maxStage={0}
                            />
                        )}
                        {actionVisibility.showPrint && (
                            <BdsRollupButton
                                id="specification-viewer-print"
                                testid="specification-viewer-print"
                                ariaLabel="Print"
                                displayType="secondary"
                                text="Print"
                                onClick={handlePrint}
                                disabled={!!actionBeingPerformed}
                                hotkey={HotkeyCommands.print.command}
                                value="print"
                                maxStage={0}
                            />
                        )}
                        {actionVisibility.showUnpublish && (
                            <BdsRollupButton
                                id="specification-viewer-unpublish"
                                testid="specification-viewer-unpublish"
                                ariaLabel="Move to draft"
                                displayType="secondary"
                                text="Move to draft"
                                onClick={() => setActiveAction("unpublish")}
                                disabled={!!actionBeingPerformed}
                                value="unpublish"
                                maxStage={0}
                            />
                        )}
                        {actionVisibility.showViewingPermissions && (
                            <BdsRollupButton
                                id="specification-viewer-viewing-permissions"
                                testid="specification-viewer-viewing-permissions"
                                ariaLabel="Viewing permissions"
                                displayType="secondary"
                                text="Viewing permissions"
                                onClick={() => setActiveAction("viewingPermissions")}
                                disabled={!!actionBeingPerformed}
                                value="viewingPermissions"
                                maxStage={0}
                            />
                        )}
                        {actionVisibility.showSaveToCatalog && (
                            <BdsRollupButton
                                id="specification-viewer-save-to-catalog"
                                testid="specification-viewer-save-to-catalog"
                                ariaLabel="Save to catalog"
                                displayType="secondary"
                                text="Save to catalog"
                                onClick={handleSaveToCatalog}
                                disabled={!!actionBeingPerformed}
                                value="saveToCatalog"
                                maxStage={0}
                            />
                        )}
                        {!isGeneralJob && actionVisibility.showPublish && (
                            <BdsRollupButton
                                id="specification-viewer-publish"
                                testid="specification-viewer-publish"
                                ariaLabel="Publish"
                                displayType="primary"
                                text="Publish"
                                onClick={() => setActiveAction("publish")}
                                disabled={!!actionBeingPerformed}
                                value="publish"
                                maxStage={0}
                            />
                        )}
                    </BdsButtonGroup>
                )}
                {!isEditMode && !selectedSnapshotId && (
                    <SpecificationInfoButton
                        id="specification-info"
                        createdBy={specification.createdBy ?? undefined}
                        createdDate={specification.createdDate}
                        lastUpdatedBy={specification.lastUpdatedBy ?? undefined}
                        lastUpdatedDate={specification.lastUpdatedDate}
                    />
                )}
                {showVersionHistoryButton && (
                    <BTButton
                        onClick={handleVersionHistoryClick}
                        data-testid="specification-viewer-version-history-button"
                        icon={<BdsIconClockCounterClockwise />}
                        type="tertiary"
                        selected={isVersionHistoryOpen}
                        aria-label="Version history"
                    />
                )}
                {actionVisibility.showShare && (
                    <BTDropdownButton
                        data-testid="specification-viewer-share"
                        type="tertiary"
                        showCaret={false}
                        buttonProps={{
                            icon: <BdsIconShare />,
                            "aria-label": "Share specification dropdown",
                        }}
                        menuItems={[
                            {
                                "data-testid": "specification-viewer-copy-share-link",
                                content: "Copy share link",
                                onClick: handleCopySpecShareLink,
                                "aria-label": "Copy share link",
                            },
                            {
                                "data-testid": "specification-viewer-create-qr-code",
                                content: "Create QR code",
                                onClick: () => setShowShareQRCode(true),
                                "aria-label": "Create QR code",
                            },
                        ]}
                    />
                )}
                {showCommentsButton && (
                    <BdsButtonGroup
                        id="specification-viewer-comments"
                        testid="specification-viewer-comments"
                        displayType="tertiary"
                    >
                        <CommentsButtonForPage
                            id="specification-comments"
                            entityId={specification.id}
                            lastUnreadCommentId={
                                discussionEntity?.discussions.oldestUnreadCommentId ?? null
                            }
                            hasUnreadComments={
                                !!discussionEntity?.discussions.oldestUnreadCommentId
                            }
                            isCommentDrawerOpen={isCommentsOpen}
                            onClick={handleCommentsClick}
                            icon={hasComments ? <BdsIconCommentsFilled /> : <BdsIconComments />}
                            text="Comments"
                            hasComments={hasComments}
                        />
                    </BdsButtonGroup>
                )}
                {actionVisibility.showEdit && (
                    <BTButton
                        onClick={handleEnterEditMode}
                        data-testid="specification-viewer-edit-button"
                        icon={<BdsIconPencilSimple />}
                        type="tertiary"
                        disabled={!!actionBeingPerformed}
                        aria-label="Edit"
                    />
                )}
            </>
        );

        const shareQRCodeModal = showShareQRCode ? (
            <BTSpecificationShareQRCode
                specificationTitle={specification.title ?? "Untitled Specification"}
                externalId={specification.externalId}
                jobId={specification.jobId}
                modalConfig={{
                    beforeClose: () => setShowShareQRCode(false),
                    parentRoute: location.pathname,
                }}
            />
        ) : null;

        const editActions = isEditMode ? (
            <BdsButtonGroup
                id="specification-viewer-edit-actions"
                testid="specification-viewer-edit-actions"
                menuTestid="specification-viewer-edit-actions-menu"
                rollupTestid="specification-viewer-edit-actions-rollup"
                className="viewer-edit-actions"
                grow
                flexAlign="end"
            >
                <BdsRollupButton
                    id="specification-viewer-cancel"
                    testid="specification-viewer-cancel-button"
                    displayType="secondary"
                    text="Cancel"
                    value="cancel"
                    disabled={!!actionBeingPerformed}
                    hotkey={HotkeyCommands.close.command}
                    onClick={handleCancel}
                    minStage={1}
                />
                <BdsRollupButton
                    id="specification-viewer-save"
                    testid="specification-viewer-save-button"
                    displayType="secondary"
                    text="Save"
                    value="save"
                    disabled={!!actionBeingPerformed}
                    loading={actionBeingPerformed === "save"}
                    hotkey={HotkeyCommands.save.command}
                    onClick={handleSubmit}
                    minStage={1}
                />
                {!isGeneralJob &&
                    canUpdate &&
                    (actionVisibility.isDraft ||
                        specification.hasUnpublishedChanges ||
                        isDirty) && (
                        <BdsRollupButton
                            id="specification-viewer-publish"
                            testid="specification-viewer-publish-button"
                            displayType="primary"
                            text="Publish"
                            value="publish"
                            disabled={!!actionBeingPerformed}
                            loading={actionBeingPerformed === "publish"}
                            onClick={() => setActiveAction("publish")}
                            minStage={1}
                        />
                    )}
            </BdsButtonGroup>
        ) : null;

        // Forces SpecificationDisplay to remount when the source of initialValues changes
        // (snapshot preview / edit / view) so the form picks up the right content.
        const getSpecificationDisplayKey = () => {
            if (isShowingSnapshotContent) return `snapshot-${selectedSnapshotId ?? "latest"}`;
            if (isEditMode) return "editing";
            return "viewing";
        };
        const specificationDisplayKey = getSpecificationDisplayKey();

        const viewerContent = (
            <div
                className="viewer-content"
                onKeyDown={isEditMode ? handleEditorArrowKeyDown : undefined}
            >
                {isSnapshotFetching ? (
                    <Skeleton active paragraph={{ rows: 8 }} className="bds-p-3" />
                ) : (
                    <StickyProvider>
                        <SpecificationDisplay
                            key={specificationDisplayKey}
                            initialValues={initialValues}
                            onSave={handleSaveSpec}
                            attachedFiles={attachedFiles}
                            canViewPhotos={specification.canViewPhotos}
                            isBuilder={isBuilder}
                            isPublished={actionVisibility.isPublished}
                            jobId={specification.jobId}
                            builderId={specification.builderId}
                            specificationId={specification.id}
                            externalId={specification.externalId}
                            readOnly={!isEditMode || isShowingSnapshotContent}
                            showViewableBySection={
                                !isExternalMode &&
                                !isEditMode &&
                                !selectedSnapshotId &&
                                !isGeneralJob
                            }
                            hasUnpublishedChanges={showUnpublishedChangesBanner}
                            showBidPackagesSection={!isExternalMode && !selectedSnapshotId}
                            isViewOnly={
                                isExternalMode || isShowingSnapshotContent || isPanelReadonly
                            }
                            isExternalMode={isExternalMode}
                            isMobileWebview={isMobileWebview}
                            activeAction={activeAction}
                            actionBeingPerformed={actionBeingPerformed}
                            onActionComplete={handleActionComplete}
                            onDirtyChange={setIsDirty}
                            onAllFileChange={setCurrentFiles}
                        />
                    </StickyProvider>
                )}
            </div>
        );

        const renderRightPanel = () => {
            if (isVersionHistoryOpen) {
                return (
                    <SpecificationVersionHistory
                        specificationExternalId={specification.externalId}
                        onVersionSelected={setSelectedSnapshotId}
                        onClose={() => setRightPanel("none")}
                        selectedSnapshotId={selectedSnapshotId}
                    />
                );
            }
            if (isCommentsOpen && !isExternalMode && !suppressRightPanels) {
                return (
                    <CommentComponent
                        entity={discussionEntity}
                        setCommentDrawerOpen={(isOpen) =>
                            setRightPanel(isOpen ? "comments" : "none")
                        }
                        onUpdateCommentData={handleUpdateCommentData}
                        isLoading={!discussionEntity}
                    />
                );
            }
            return null;
        };
        const rightPanelContent = renderRightPanel();

        const splitViewMode: BTSplitViewMode =
            isVersionHistoryOpen || isCommentsOpen ? "split" : "list";

        // Show the snapshot's title when previewing a historical version, otherwise the live title.
        const viewerTitle =
            activeSnapshotData?.title ?? specification.title ?? "Untitled Specification";

        if (isPanelMode) {
            const panelContent = (
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- migrate BdsLayout to plain flex layout (ADO 271961)
                <BdsLayout displayType="HCF" useMaxWidth className="viewer-layout">
                    <BTItemHeader
                        testid="specification-viewer-header"
                        className={cn({
                            "viewer-header--panel": !isEditMode,
                            "viewer-header--hidden": isEditMode,
                        })}
                        ariaLabel="spec-viewer-header"
                    >
                        {!isEditMode && panelConfig.title && (
                            <div className="viewer-header--panel-title-group mr-auto">
                                <BTText
                                    tag="h2"
                                    size="distinct-lg"
                                    className="viewer-header--panel-title"
                                    testid="specification-viewer-panel-title"
                                    text={viewerTitle}
                                />
                                {panelConfig.status && !isExternalMode && (
                                    <div className="viewer-header--panel-status">
                                        <StatusTagDisplay
                                            statusType={isPublished ? "success" : ""}
                                            statusText={isPublished ? "Published" : "Draft"}
                                            data-testid="specification-viewer-panel-status"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {!isEditMode && headerActions}
                        {!isEditMode && onBack && (
                            <BTButton
                                onClick={handleBack}
                                data-testid="specification-viewer-panel-close-button"
                                icon={<BdsIconX />}
                                type="tertiary"
                                aria-label="Close"
                            />
                        )}
                    </BTItemHeader>
                    {viewerContent}
                    {editActions && (
                        <BdsActionBar
                            id="specification-viewer-panel-footer"
                            testid="specification-viewer-panel-footer"
                            grow
                        >
                            {editActions}
                        </BdsActionBar>
                    )}
                </BdsLayout>
            );

            // Always render BTSplitView and toggle `mode` rather than mounting it fresh — a
            // freshly-mounted split starts hidden until its ResizeObserver measures it, flashing gray.
            const hasRightPanels = panelConfig.versionHistory || panelConfig.comments;
            const panelSplitMode: BTSplitViewMode = hasRightPanels ? splitViewMode : "list";

            // When the panel is too narrow for side-by-side AND Version History is open, take over
            // the whole panel (the split's spec-side `list` pane has no room). Mirrors the book
            // view's narrow-viewport branch: show the snapshot preview once a version is selected,
            // otherwise the version list. Comments have no sub-navigation, so they just use the
            // split's own responsive collapse instead of a takeover.
            const showVersionHistoryTakeover = isPanelNarrow && isVersionHistoryOpen;
            const versionHistoryTakeover = selectedSnapshotId ? (
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- migrate BdsLayout/BdsContentHeader to BTItemHeader + plain flex layout (ADO 271961)
                <BdsLayout displayType="HCF" useMaxWidth className="viewer-layout">
                    {/* eslint-disable-next-line @typescript-eslint/no-deprecated -- migrate BdsLayout/BdsContentHeader to BTItemHeader + plain flex layout (ADO 271961) */}
                    <BdsContentHeader
                        id="panel-snapshot-header"
                        testid="panel-snapshot-header"
                        title="Version preview"
                        slotTop={
                            <BTButton
                                onClick={() => setSelectedSnapshotId(null)}
                                data-testid="panel-snapshot-back-button"
                                icon={<BdsIconX />}
                                type="tertiary"
                                aria-label="Back to version history"
                            />
                        }
                    />
                    {viewerContent}
                </BdsLayout>
            ) : (
                <div className="viewer-layout">{rightPanelContent}</div>
            );

            // Pick the panel body up front with early returns (rather than a nested ternary inline):
            //  - no right panels enabled → the plain spec content (linked-spec drawer / catalog)
            //  - narrow panel + Version History open → full-panel takeover
            //  - otherwise → the resizable spec | VH/Comments split
            const renderPanelBody = () => {
                if (!hasRightPanels) {
                    return panelContent;
                }
                if (showVersionHistoryTakeover) {
                    return versionHistoryTakeover;
                }
                return (
                    <BTSplitView
                        testid="specification-viewer-panel-split"
                        name="Specification"
                        mode={panelSplitMode}
                        list={panelContent}
                        detail={rightPanelContent ?? <></>}
                        maxDetailSize={isCommentsOpen ? 50 : 30}
                        responsiveCollapse={isCommentsOpen}
                        persistenceId="spec-panel-detail"
                    />
                );
            };

            return (
                <div
                    ref={panelWrapperRefHandler}
                    className={cn("specification-viewer", "panelMode", {
                        editing: isEditMode,
                        creating: isCreating,
                    })}
                    data-testid="specification-viewer"
                >
                    {renderPanelBody()}
                    {shareQRCodeModal}
                </div>
            );
        }

        const specContent = (
            // eslint-disable-next-line @typescript-eslint/no-deprecated -- migrate BdsLayout/BdsContentHeader to BTItemHeader + plain flex layout (ADO 271961)
            <BdsLayout displayType="HCF" useMaxWidth className="viewer-layout">
                {/* eslint-disable-next-line @typescript-eslint/no-deprecated -- migrate BdsLayout/BdsContentHeader to BTItemHeader + plain flex layout (ADO 271961) */}
                <BdsContentHeader
                    id="specification-viewer-header"
                    testid="specification-viewer-header"
                    className={isEditMode && !onBack ? "viewer-header--hidden" : undefined}
                    title={isEditMode ? <></> : viewerTitle}
                    jobName={useMobileLayout ? displayJobName : undefined}
                    slotTop={
                        <>
                            {headerActions}
                            {onBack && (
                                <BTButton
                                    onClick={handleBack}
                                    data-testid="specification-viewer-close-button"
                                    icon={<BdsIconX />}
                                    type="tertiary"
                                    aria-label="Close"
                                />
                            )}
                        </>
                    }
                    {...statusProps}
                />
                {viewerContent}
                {editActions && (
                    <BdsActionBar
                        id="specification-viewer-footer"
                        testid="specification-viewer-footer"
                        grow
                    >
                        {editActions}
                    </BdsActionBar>
                )}
            </BdsLayout>
        );

        // On narrow viewports, version history replaces spec content instead of split view.
        if (useMobileVersionHistoryPanel && isVersionHistoryOpen) {
            return (
                <div
                    className="specification-viewer mobile-layout"
                    data-testid="specification-viewer"
                >
                    {selectedSnapshotId ? (
                        // Snapshot preview full-screen
                        // eslint-disable-next-line @typescript-eslint/no-deprecated -- migrate BdsLayout/BdsContentHeader to BTItemHeader + plain flex layout (ADO 271961)
                        <BdsLayout displayType="HCF" useMaxWidth className="viewer-layout">
                            {/* eslint-disable-next-line @typescript-eslint/no-deprecated -- migrate BdsLayout/BdsContentHeader to BTItemHeader + plain flex layout (ADO 271961) */}
                            <BdsContentHeader
                                id="mobile-snapshot-header"
                                testid="mobile-snapshot-header"
                                title="Version preview"
                                slotTop={
                                    <BTButton
                                        onClick={() => setSelectedSnapshotId(null)}
                                        data-testid="mobile-snapshot-back-button"
                                        icon={<BdsIconX />}
                                        type="tertiary"
                                        aria-label="Back to version history"
                                    />
                                }
                            />
                            {viewerContent}
                        </BdsLayout>
                    ) : (
                        // Version list full-screen
                        <div className="viewer-layout">
                            <SpecificationVersionHistory
                                specificationExternalId={specification.externalId}
                                onVersionSelected={setSelectedSnapshotId}
                                onClose={() => setRightPanel("none")}
                                selectedSnapshotId={selectedSnapshotId}
                            />
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                className={cn("specification-viewer", {
                    "mobile-layout": useMobileLayout,
                    editing: isEditMode,
                    creating: isCreating,
                })}
                data-testid="specification-viewer"
            >
                <BTSplitView
                    // Remount on panel switch so the split reinitializes with the correct default size
                    key={rightPanel}
                    testid="specificationSplitView"
                    name="Specification"
                    mode={splitViewMode}
                    list={specContent}
                    detail={rightPanelContent ?? <></>}
                    // Comments 50/50, version history 30/70
                    maxDetailSize={isCommentsOpen ? 50 : 30}
                    responsiveCollapse={isCommentsOpen}
                    persistenceId={isCommentsOpen ? "spec-comments" : "spec-version-history"}
                />
                {shareQRCodeModal}
            </div>
        );
    },
);
